import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { ISiteListDetails } from '@shared/shared/interfaces/site-settings.interfaces';
import { USER_LOCAL_STORAGE_KEY, USER_SELECTED_SITE } from 'app.config';
import { applyNonceToElement, preserveExistingNonceValues } from 'core/utils/csp-nonce';
import { AppModule } from './app.module';
import { environment } from './environment';

/**
 * The config.json contains environment-specific configs which can be changed AFTER build.
 *
 * It is the deploy job's responsibility to rename the config.<env>.json to config.json
 */

// Preserve any existing nonce values before Angular bootstraps
preserveExistingNonceValues();

fetch('./assets/config/config.json', { cache: 'no-cache' })
  .then(async (response) => {
    const dynamicConfig = await response.json();

    Object.assign(environment, dynamicConfig);

    if (environment.production) {
      enableProdMode();
    }

    // For MTLS you now can bypass the site selector by appending the country and lang to the homepage URL
    // /product/mastercard/homepage/gb/en-gb
    // https://tenproduct.atlassian.net/browse/TPD-18114
    const URLParts = getUrlPathname().split('/');
    let country = '';
    let language = '';
    let cleanURL = '';
    const pathname = window.location.pathname;

    if (!pathname.includes('/homepage') && pathname !== '/') {
      localStorage.setItem('initPath', pathname);
    }

    if (window.location.pathname.includes('/homepage') && URLParts.length === 6) {
      language = URLParts.pop();
      country = URLParts.pop();
      cleanURL = URLParts.join('/');

      await getSites().then((results) => {
        const siteObj: ISiteListDetails[] = results.filter(
          (x: ISiteListDetails) => x.language === language && x.country_code.toLowerCase() === country
        );

        if (!siteObj.length) {
          window.location.href = cleanURL;
        }

        document.cookie = `selectedSite=${siteObj[0].site_id};path=/`;
        localStorage.setItem(USER_SELECTED_SITE, siteObj[0].site_id.toString());
      });
    }

    if (localStorage.getItem(USER_LOCAL_STORAGE_KEY)) {
      appendMapBoxJs();
    }

    platformBrowserDynamic()
      .bootstrapModule(AppModule)
      .catch((err) => console.error(err));
  })
  .catch((reason) => {
    console.error('[ERROR] config.json could not be loaded', reason);
  });

async function getSites() {
  return await fetch(ApiEndpoint.SELF_REGISTRATION_SITES).then((response) => response.json());
}

function getUrlPathname() {
  if (window.location.pathname.slice(-1) === '/') {
    return window.location.pathname.substring(0, window.location.pathname.length - 1);
  }
  return window.location.pathname;
}

function appendMapBoxJs() {
  const script = document.createElement('script');
  script.crossOrigin = 'anonymous';
  script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl-csp.js';
  script.integrity = 'sha384-5HqSlPgN9dtKFIrcNzCSNJzTdvU3zt4+583CVhg024j3WK321roS8ZgwRNKJl9cQ';
  applyNonceToElement(script);
  document.head.appendChild(script);
}

export function getNonceValue() {
  return document.querySelector('meta[name="csp-nonce"]')?.getAttribute('value');
}

document.addEventListener('securitypolicyviolation', (e) => {
  const reportToUrl = '/v1/analytics/csp_violation_report/';
  fetch(reportToUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/csp-report',
    },
    body: JSON.stringify({
      'csp-report': {
        'blocked-uri': e.blockedURI,
        'document-uri': e.documentURI,
        'original-policy': e.originalPolicy,
        'referrer': e.referrer,
        'script-sample': e.sample,
        'source-file': e.sourceFile,
        'violated-directive': e.violatedDirective,
      },
    }),
    credentials: 'omit',
    cache: 'no-cache',
    referrerPolicy: 'no-referrer',
  });
});
